import useClassNames from '@medflex/hooks/useClassNames';
import useDisableRubberBandScrolling from '@medflex/hooks/useDisableRubberBandScrolling';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import Button from './Button';
import { IconClose } from './Icons';
import Portal from './Portal';
import IconInformationOutline from './icons/InformationOutline';
import Link from './Link';

const Modal = ({
  children,
  showCloseButton,
  onClickCloseButton = () => null,
  onClickOutside = () => null,
  isFullScreen,
  className,
  customBackground,
  title,
  titleButton,
  infoUrl,
  dropTarget,
  isScrollable,
  ...props
}) => {
  const modalBackdropRef = React.useRef();
  const cn = useClassNames(
    () => [
      'fixed',
      'w-full',
      'max-h-full',
      'lg:mt-0',
      'bottom-0',
      'lg:bottom-auto',
      'left-0',
      'flex',
      'flex-col',
      'min-h-0',
      'right-0',
      'mx-auto',
      'rounded-t-lg',
      'lg:top-0',
      'z-30',
      isFullScreen && 'items-center',
      isFullScreen && 'lg:pt-6',
      !isFullScreen && 'pt-6',
      isFullScreen && 'pt-6 lg:pt-0',
      !isFullScreen && 'lg:top-1/2',
      !isFullScreen && 'lg:-translate-y-1/2',
      !isFullScreen && 'lg:max-w-2xl',
      !isFullScreen && 'lg:rounded-lg',
      !isFullScreen && 'shadow-lg',
      !customBackground && 'bg-white',
      customBackground && customBackground,
      className,
    ],
    [className, isFullScreen, customBackground],
  );
  const bdcn = useClassNames(
    () => [
      'fixed',
      'inset-0',
      'z-20',
      'space-y-4',
      !isFullScreen && 'bg-slate-700',
      !isFullScreen && 'opacity-50',
      isFullScreen && 'opacity-50 lg:opacity-100 bg-slate-700',
      isFullScreen && 'lg:bg-white',
    ],
    [isFullScreen],
  );

  const onClickOutsideHandler = event => {
    if (onClickOutside && event.target === modalBackdropRef.current) {
      onClickOutside(event);
    }
  };

  const modalRef = useDisableRubberBandScrolling();

  React.useEffect(() => {
    document.addEventListener('click', onClickOutsideHandler, { capture: true });
    return () => {
      document.removeEventListener('click', onClickOutsideHandler, { capture: true });
    };
  }, []);

  return (
    <Portal>
      <div ref={modalBackdropRef} data-component="ModalBackdrop" className={bdcn} />
      <div data-component="Modal" className={cn} {...props} ref={dropTarget}>
        {showCloseButton && (
          <div className="absolute top-0 right-0">
            <Button
              size="sm"
              shape="circle"
              kind="secondary:ghost"
              className="block mt-2 mr-2"
              onClick={onClickCloseButton}
            >
              <IconClose size="4" />
            </Button>
          </div>
        )}
        <div className="flex flex-col max-h-full flex-1 min-h-0 overflow-hidden">
          {title && (
            <header className="flex items-center space-x-2 px-4 lg:px-8 mb-4 lg:mb-8">
              <h2 className="text-xl lg:text-2xl font-semibold">{title}</h2>
              {infoUrl && (
                <Link href={infoUrl} target="_blank">
                  <IconInformationOutline size="5" />
                </Link>
              )}
              {titleButton && titleButton}
            </header>
          )}
          <div
            ref={modalRef}
            className={`${
              isScrollable ? 'overflow-y-auto' : ''
            } max-h-full flex flex-col min-h-0 px-4 lg:px-8 pb-4 lg:pb-8`}
          >
            {children}
          </div>
        </div>
      </div>
    </Portal>
  );
};

Modal.defaultProps = {
  showCloseButton: false,
  isFullScreen: false,
  dropTarget: null,
  className: '',
  customBackground: null,
  title: null,
  infoUrl: null,
  children: null,
  titleButton: null,
  isScrollable: true,
  onClickOutside: () => {},
  onClickCloseButton: () => {},
};

Modal.propTypes = {
  onClickOutside: PropTypes.func,
  showCloseButton: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  onClickCloseButton: PropTypes.func,
  children: PropTypes.node,
  titleButton: PropTypes.node,
  customBackground: PropTypes.string,
  title: PropTypes.string,
  infoUrl: PropTypes.string,
  dropTarget: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  isScrollable: PropTypes.bool,
};

export default Modal;
